import { Search, UserSearch } from "lucide-react";
import { ClientsTable } from "@/components/clients/ClientsTable";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useBuInstanceLabel } from "@/hooks/use-bu-instance-label";
import type { SearchClientsResult } from "@/lib/coach-manager/client";

interface ClientSearchResultsProps {
  result: SearchClientsResult | undefined;
  isPending: boolean;
  error: Error | null;
}

/**
 * Renders every state of a client search: idle prompt, loading, a
 * route/transport-level error, a Coach-Manager-level failure (`ok: false`),
 * zero matches, or the matched clients as a matrix table. Mirrors
 * `ContractSearchResults` exactly.
 */
export function ClientSearchResults({ result, isPending, error }: ClientSearchResultsProps) {
  const { label } = useBuInstanceLabel();
  if (isPending) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (error) {
    return <ErrorState message={error.message} />;
  }

  if (!result) {
    return (
      <EmptyState
        icon={Search}
        title="Search for a client"
        description={`Pick a ${label} and enter search criteria above.`}
      />
    );
  }

  if (!result.ok) {
    return <ErrorState message={result.error} />;
  }

  if (result.clients.length === 0) {
    return (
      <EmptyState
        icon={UserSearch}
        title="No clients found"
        description="Try different search criteria."
      />
    );
  }

  return <ClientsTable clients={result.clients} />;
}
